import React, { useEffect, useState } from 'react';
import { Loader2, RefreshCw, CheckCircle2, XCircle, Send, Instagram, Facebook, PlugZap } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useToast } from '../../hooks/use-toast';
import { api } from '../../context/AuthContext';

function describeError(e) {
  const status = e?.response?.status;
  const detail = e?.response?.data?.detail;
  let body = '';
  if (typeof detail === 'string') body = detail;
  else if (detail && typeof detail === 'object') body = detail.message || JSON.stringify(detail);
  else if (e?.message) body = e.message;
  return status ? `HTTP ${status} — ${body}` : (body || 'Network error');
}

function fmtWhen(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function SubStatusChip({ label, status }) {
  if (!status) {
    return <Badge variant="outline" className="text-[10px]">— {label}</Badge>;
  }
  if (status.ok) {
    return (
      <Badge className="bg-brand-green/15 text-brand-green border-brand-green/30 text-[10px]">
        <CheckCircle2 className="w-3 h-3 mr-1" /> {label}
      </Badge>
    );
  }
  const title = status.message || `code ${status.code || '?'}`;
  return (
    <Badge className="bg-red-100 text-red-800 border-red-300 text-[10px]" title={title}>
      <XCircle className="w-3 h-3 mr-1" /> {label} failed
    </Badge>
  );
}

export default function CrmAdminMeta() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [laneEnabled, setLaneEnabled] = useState(true);
  const [bindings, setBindings] = useState([]);
  const [retrying, setRetrying] = useState('');
  const [testOpen, setTestOpen] = useState(false);
  const [testTarget, setTestTarget] = useState(null); // the binding row
  const [testChannel, setTestChannel] = useState('messenger_dm');
  const [testRecipient, setTestRecipient] = useState('');
  const [testComment, setTestComment] = useState('');
  const [testText, setTestText] = useState(
    '[LISTING AiGENT diagnostic] This is a test from your realtor admin — ignore. Timestamp: __NOW__',
  );
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/meta/bindings');
      setBindings(data.bindings || []);
      setLaneEnabled(true);
    } catch (e) {
      if (e?.response?.status === 404) {
        // Router isn't mounted — META_DIRECT_ENABLED flag is off.
        setLaneEnabled(false);
        setBindings([]);
      } else {
        toast({ title: 'Failed to load Meta bindings', description: describeError(e), variant: 'destructive' });
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const retrySubscribe = async (realtorId) => {
    setRetrying(realtorId);
    try {
      await api.post(`/admin/meta/bindings/${realtorId}/retry-subscribe`, {});
      toast({ title: 'Subscription re-run', description: 'Status refreshed below.' });
      await load();
    } catch (e) {
      toast({ title: 'Retry failed', description: describeError(e), variant: 'destructive' });
    } finally { setRetrying(''); }
  };

  const openTest = (row) => {
    setTestTarget(row);
    setTestChannel(row.ig_user_id ? 'ig_dm' : 'messenger_dm');
    setTestRecipient('');
    setTestComment('');
    setLastResult(null);
    setTestOpen(true);
  };

  const sendTest = async () => {
    if (!testTarget) return;
    setSending(true);
    setLastResult(null);
    try {
      const body = { channel: testChannel, text: testText };
      if (testChannel === 'ig_private_reply') body.comment_id = testComment;
      else body.recipient_id = testRecipient;
      const { data } = await api.post(
        `/admin/meta/bindings/${testTarget.realtor_id}/test-dm`,
        body,
      );
      setLastResult(data);
      toast({
        title: data.ok ? 'Test DM sent' : 'Test DM failed',
        description: data.ok ? 'Graph API returned success.' : (data.message || `code ${data.code || data.status}`),
        variant: data.ok ? undefined : 'destructive',
      });
    } catch (e) {
      toast({ title: 'Send failed', description: describeError(e), variant: 'destructive' });
    } finally { setSending(false); }
  };

  return (
    <div className="space-y-6" data-testid="meta-admin-page">
      <Card className="p-5 bg-gradient-to-br from-brand-cream/40 to-white">
        <div className="flex items-start gap-3">
          <PlugZap className="w-6 h-6 text-brand-green mt-0.5" />
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-serif text-brand-green">Meta Direct — Admin Diagnostics</h2>
            <p className="text-sm text-brand-green/70 mt-1">
              Inspect every realtor's Facebook / Instagram binding, retry a stuck webhook
              subscription, and fire a one-click test DM through our stored page token to
              verify the lane end-to-end.
            </p>
          </div>
          <Button variant="outline" onClick={load} data-testid="btn-refresh-bindings">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>
      </Card>

      {loading ? (
        <Card className="p-6">
          <div className="flex items-center gap-2 text-brand-green/70">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading Meta bindings…
          </div>
        </Card>
      ) : !laneEnabled ? (
        <Card className="p-6 border-amber-300 bg-amber-50" data-testid="meta-lane-disabled">
          <h3 className="font-serif text-lg text-amber-900">Meta Direct lane is not enabled yet</h3>
          <p className="text-sm text-amber-900/80 mt-1">
            Set <code className="font-mono bg-amber-100 px-1">META_DIRECT_ENABLED=true</code> in the
            backend <code className="font-mono bg-amber-100 px-1">.env</code> and redeploy once Meta
            App Review grants Advanced Access to <code className="font-mono">pages_messaging</code>{' '}
            and <code className="font-mono">instagram_manage_messages</code>. This page will light
            up automatically — no code change needed.
          </p>
        </Card>
      ) : bindings.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-sm text-brand-green/70">
            No realtors have connected a Meta account yet. Flows that need
            testing should start with a realtor completing{' '}
            <code className="font-mono">/api/meta/oauth/start</code>.
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {bindings.map((b) => (
            <Card key={b.realtor_id} className="p-4" data-testid={`binding-row-${b.realtor_id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Facebook className="w-4 h-4 text-brand-green" />
                    <span className="font-medium text-brand-green">{b.page_name || b.page_id}</span>
                    {b.ig_username && (
                      <>
                        <span className="text-brand-green/40">·</span>
                        <Instagram className="w-4 h-4 text-brand-green" />
                        <span className="font-medium text-brand-green">@{b.ig_username}</span>
                      </>
                    )}
                  </div>
                  <p className="text-xs text-brand-green/60 mt-1">
                    Realtor: <span className="font-medium">{b.realtor_name || b.realtor_email || b.realtor_id}</span>
                    {' '}· page_id <code className="font-mono">{b.page_id}</code>
                    {b.ig_user_id && <> · ig_user_id <code className="font-mono">{b.ig_user_id}</code></>}
                  </p>
                  <p className="text-[11px] text-brand-green/50 mt-1">
                    Bound {fmtWhen(b.bound_at)} · subscription checked {fmtWhen(b.subscription_updated_at)}
                  </p>
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <SubStatusChip label="Page" status={b.subscription_status?.page} />
                    <SubStatusChip label="Instagram" status={b.subscription_status?.instagram} />
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => retrySubscribe(b.realtor_id)}
                    disabled={retrying === b.realtor_id}
                    data-testid={`btn-retry-${b.realtor_id}`}
                  >
                    {retrying === b.realtor_id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                    Retry subscribe
                  </Button>
                  <Button size="sm" onClick={() => openTest(b)} data-testid={`btn-test-${b.realtor_id}`}>
                    <Send className="w-3 h-3 mr-1" /> Send test DM
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={testOpen} onOpenChange={setTestOpen}>
        <DialogContent className="max-w-xl" data-testid="test-dm-dialog">
          <DialogHeader>
            <DialogTitle>Send a diagnostic DM</DialogTitle>
            <DialogDescription>
              Fires a Graph API call using{' '}
              <span className="font-medium">{testTarget?.page_name || testTarget?.page_id}</span>'s stored page token.
              Returns the raw Graph response below so you can read error codes directly.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Label>Channel</Label>
              <Select value={testChannel} onValueChange={setTestChannel}>
                <SelectTrigger data-testid="select-channel"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="messenger_dm">Messenger DM (PSID)</SelectItem>
                  {testTarget?.ig_user_id && <SelectItem value="ig_dm">Instagram DM (IGSID)</SelectItem>}
                  {testTarget?.ig_user_id && <SelectItem value="ig_private_reply">IG private reply to a comment</SelectItem>}
                </SelectContent>
              </Select>
            </div>

            {testChannel === 'ig_private_reply' ? (
              <div>
                <Label htmlFor="test-comment">Comment ID</Label>
                <Input id="test-comment" value={testComment} onChange={(e) => setTestComment(e.target.value)}
                       placeholder="e.g. 17895695668004550" data-testid="input-comment-id" />
              </div>
            ) : (
              <div>
                <Label htmlFor="test-recipient">
                  Recipient ID — {testChannel === 'ig_dm' ? 'IGSID (IG-scoped subscriber id)' : 'PSID (Messenger page-scoped id)'}
                </Label>
                <Input id="test-recipient" value={testRecipient} onChange={(e) => setTestRecipient(e.target.value)}
                       placeholder="e.g. 24680123456789" data-testid="input-recipient-id" />
                <p className="text-[11px] text-brand-green/60 mt-1">
                  You can get a PSID / IGSID from the webhook payload of a prior inbound DM, or by messaging the realtor's
                  Page from an admin-controlled test account and reading the event in Meta's dev dashboard.
                </p>
              </div>
            )}

            <div>
              <Label htmlFor="test-text">Message body</Label>
              <Textarea id="test-text" rows={4} value={testText}
                        onChange={(e) => setTestText(e.target.value)}
                        className="font-mono text-xs" data-testid="input-text" />
              <p className="text-[11px] text-brand-green/60 mt-1">
                <code className="font-mono">__NOW__</code> is replaced by the current UTC timestamp server-side.
              </p>
            </div>

            {lastResult && (
              <div className="rounded border bg-brand-cream/40 p-3 text-xs max-h-56 overflow-auto" data-testid="test-result">
                <div className={lastResult.ok ? 'text-brand-green' : 'text-red-700'}>
                  {lastResult.ok ? '✓ sent' : `✗ failed (code ${lastResult.code || lastResult.status || '?'})`}
                </div>
                <pre className="whitespace-pre-wrap font-mono mt-1">{JSON.stringify(lastResult, null, 2)}</pre>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setTestOpen(false)} disabled={sending}>Close</Button>
            <Button onClick={sendTest} disabled={sending} data-testid="btn-send-test-dm">
              {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
              Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
